import React, { FC } from 'react';
import styled from 'styled-components';
import theme from '../../../shared/theme';

export type ZSwitchVariant = 'default' | 'ai';

export interface ZSwitchProps {
    ariaLabel?: string;
    checked?: boolean;
    className?: string;
    disabled?: boolean;
    /** Keeps the switch looking disabled while still emitting onChange, so the caller can react to the click (e.g. open an upgrade dialog). */
    allowChangeOnDisabled?: boolean;
    id?: string;
    name?: string;
    onChange?: (checked: boolean, event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>, id?: string) => void;
    variant?: ZSwitchVariant;
}

const SwitchWrapper = styled.div<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 50px;
    background: transparent;
`;

const SwitchButton = styled.button<{ $checked?: boolean; $disabled?: boolean }>`
    position: relative;
    width: 32px;
    height: 16px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 50px;
    background: transparent;
    cursor: pointer;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.3);
    pointer-events: auto;

    ${(props) =>
        props.$disabled &&
        `
        cursor: not-allowed;
        opacity: 0.55;
    `}
`;

const SwitchBackground = styled.span<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: absolute;
    inset: 0;
    border-radius: inherit;
    transition: background 0.3s ease;
    background: ${({ $checked, $variant }) => {
        if (!$checked) return '#888';
        if ($variant === 'ai') return theme.ai.gradient;
        return `color-mix(in srgb, ${theme.app.secondary} 80%, white)`;
    }};
`;

const SwitchHandle = styled.span<{ $checked?: boolean; $variant: ZSwitchVariant }>`
    position: absolute;
    top: 0;
    left: ${(props) => (props.$checked ? '16px' : '0')};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    box-sizing: border-box;
    transition: all 0.3s ease;
    background: ${({ $checked, $variant }) => {
        if (!$checked) return '#fff';
        if ($variant === 'ai') return '#fff';
        return theme.app.secondary;
    }};
    border: ${({ $checked, $variant }) => {
        if (!$checked) return '1px solid #888';
        if ($variant === 'ai') return `1px solid ${theme.ai.gradientMid}`;
        return 'none';
    }};
    z-index: 1;
`;

export const ZSwitch: FC<ZSwitchProps> = ({ variant = 'default', ariaLabel, checked, className, disabled, allowChangeOnDisabled, id, name, onChange }) => {
    // A `disabled` button emits no pointer events at all, so the attribute is dropped when the caller
    // still wants the click. The switch keeps its disabled look and is announced as disabled.
    const isInert = disabled && !allowChangeOnDisabled;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isInert) return;
        onChange?.(!checked, event, id);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (isInert || (event.key !== 'Enter' && event.key !== ' ')) return;

        event.preventDefault();
        onChange?.(!checked, event, id);
    };

    return (
        <SwitchWrapper $checked={checked} $variant={variant} className={className}>
            <SwitchButton
                id={id}
                name={name}
                type='button'
                role='switch'
                aria-checked={!!checked}
                aria-label={ariaLabel}
                disabled={isInert}
                aria-disabled={disabled}
                $disabled={disabled}
                $checked={checked}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <SwitchBackground $checked={checked} $variant={variant} />
                <SwitchHandle $checked={checked} $variant={variant} />
            </SwitchButton>
        </SwitchWrapper>
    );
};
